<template>
    <template-editor-page-layout
        :is-loading="isLoading"
        name="documentTemplates"
        content-class="flex-column"
        @save="save"
        @save-new="saveNew"
        @change-language="changeLanguage"
    >
        <template v-slot:appBar="{isNewTemplate}">
            <v-form ref="form" v-model="valid" class="mr-4 d-flex">
                <v-text-field
                    v-if="isNewTemplate"
                    v-model="documentType"
                    :label="`${$t('type')}*`"
                    :rules="[requiredRule('type')]"
                    min-width="150"
                    density="compact"
                    validate-on="blur"
                    class="mr-4"
                />
                <v-text-field
                    v-model="documentTitle"
                    :label="`${$t('title')}*`"
                    :rules="[requiredRule('title')]"
                    min-width="150"
                    density="compact"
                    validate-on="blur"
                />
            </v-form>
            <v-select
                v-model="documentLanguage"
                :items="templateLocales"
                :disabled="isNewTemplate || isLoading"
                max-width="80"
                density="compact"
                class="mr-4"
                @update:model-value="changeLanguage"
            />
            <keep-alive>
                <div class="mr-4"><code-editor-dialog v-model="template.htmlTemplate" /></div>
            </keep-alive>
        </template>
        <keep-alive>
            <HtmlEditor v-if="template.htmlTemplate" v-model="template.htmlTemplate" />
        </keep-alive>
    </template-editor-page-layout>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import TemplateEditorPageLayout from '@/components/admin/TemplateEditorPageLayout.vue'
    import {TemplateEditorPageMixin} from '@/components/account/mixins/TemplateEditorPageMixin.vue'
    import {DocumentTemplatesMixin} from '@/components/account/mixins/DocumentTemplatesMixin.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import CodeEditorDialog from '@/components/admin/modals/CodeEditorDialog.vue'

    @Component({
        components: {
            CodeEditorDialog,
            HtmlEditor: defineAsyncComponent(() => import('~/components/admin/DocumentEditor.vue')),
            TemplateEditorPageLayout,
        },
        async setup() {
            await useAsyncData('document-editor', async () => {
                useHead(DocumentEditorPage.__vccOpts.methods.head())
                return true
            })
        },
    })
    export class DocumentEditorPage extends mixins(
        DocumentTemplatesMixin,
        TemplateEditorPageMixin,
        FormValidationRulesMixin
    ) {
        template = {
            htmlTemplate: '',
        }
        documentLanguage = null
        documentTitle = null
        documentType = null
        isNewTemplate = false
        valid = false

        head() {
            return {
                bodyAttrs: {
                    'data-editor': 'DecoupledDocumentEditor',
                    'data-collaboration': false,
                    'data-revision-history': false,
                },
            }
        }

        async mounted() {
            this.isLoading = true
            if (!this.$route.params.id) {
                this.isNewTemplate = true
                this.template = {
                    active: false,
                    documentVisibility: ['TOUR_OPERATOR_1LEVEL'],
                    localeCode: 'en',
                    htmlTemplate: '<h3 class="text-center">Here you can add your html code</h3>',
                }
                this.documentLanguage = 'en'
                this.isLoading = false
                return
            }
            await this.loadDocumentTemplates({locale: this.locale, id: +this.$route.params.id})
            this.template = this.documentTemplates[0]
            if (!this.template) {
                throw createError({statusCode: 404})
            }

            this.documentLanguage = this.template.templateLocales.includes(this.locale) ? this.locale : 'en'
            this.documentTitle = this.template.title
            this.isLoading = false
        }

        async changeLanguage() {
            this.isLoading = true
            await this.loadDocumentTemplates({id: this.template.id, locale: this.documentLanguage})
            this.template = this.documentTemplates[0]
            this.documentTitle = this.template.title
            this.isLoading = false
        }

        async save() {
            await this.$refs.form.validate()
            if (!this.valid) return

            try {
                this.isLoading = true
                await this.$api.documentTemplates.put(this.template.id, {
                    localeCode: this.documentLanguage,
                    htmlTemplate: this.template.htmlTemplate,
                    title: this.documentTitle,
                })
                await this.$router.push({name: 'documentTemplates'})
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.isLoading = false
            }
        }

        async saveNew() {
            await this.$refs.form.validate()
            if (!this.valid) return

            try {
                this.isLoading = true
                await this.$api.documentTemplates.post({
                    ...this.template,
                    localeCode: this.documentLanguage,
                    htmlTemplate: this.template.htmlTemplate,
                    title: this.documentTitle,
                    documentType: this.documentType,
                })
                await this.$router.push({name: 'documentTemplates'})
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.isLoading = false
            }
        }

        get templateLocales() {
            return this.$conf.public.locales
        }
    }

    export default DocumentEditorPage
</script>
